/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {CommonLoading} from '../../../../Utils/commonLoading'
import {ProfileImg} from '../../../../Utils/fileUrlHelper'
import axios from 'axios'
import Select from 'react-select'
import CallBillingTab from './components/CallBillingTab'
import EditInterpreterLanguageModal from './components/EditInterpreterLanguageModal'
import clsx from 'clsx'
import {useSelector} from 'react-redux'
import type {RootState} from '../../../redux/store'
import {useAuth} from '../../../modules/auth'
import {Modal} from 'bootstrap'
import toaster from '../../../../Utils/toaster'
import {Overlay, Tooltip} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import ScheduledCallActivitiesTab from './components/ScheduledCallActivitiesTab'
import AiQcStatusBadge from '../../../UI/configuration-management/components/AiQcStatusBadge'
import CallTagsSection, {
  CallTag,
  CallTagBadges,
} from './components/CallTagsSection'

const API_URL = process.env.REACT_APP_API_URL

function getBackstopPartnerDisplay(item: any): string {
  const name =
    item?.backstopPartner ??
    item?.BackstopPartner ??
    item?.backstopPartnerName ??
    item?.BackstopPartnerName ??
    ''
  const trimmed = name != null ? String(name).trim() : ''
  return trimmed || '-'
}

// The API returns isAiInterpretation as 0/1 (int?), so it must not be compared with === true
function isAiInterpretationFlag(row: any): boolean {
  const raw = row?.isAiInterpretation ?? row?.IsAiInterpretation ?? row?.isAIInterpretation
  if (raw === true || raw === 'true') return true
  return Number(raw) === 1
}

type Props = {
  className: string
  isLoading: boolean
  call_log: any
  tableRef: any
  title?: string
  onRefresh?: () => void
  /** When set, fetch and show the Details modal for this call (e.g. from AI QC). */
  externalCallOpen?: {
    callId: string
    initialContactId?: string | null
    rowItem?: any
  } | null
  onExternalCallOpened?: () => void
  /** Render only the Details modal (no table body). */
  hideTable?: boolean
}

const LogsViewTable: React.FC<Props> = ({
  className,
  isLoading,
  call_log,
  tableRef,
  title,
  onRefresh,
  externalCallOpen,
  onExternalCallOpened,
  hideTable = false,
}) => {
  const target = useRef(null)
  const fetchCallIdRef = useRef<string | null>(null)
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const isSystemUser = currentUser?.result?.userType === 'SYSTEM'
  const isConsumer = currentUser?.result?.userType === 'CONSUMER'
  const showAiQcColumn =
    isSystemUser &&
    (title === 'OPI Logs' || title === 'On Demand Log' || title === 'Scheduled Log')

  const getInterpreterIdDisplay = (row: any): string => {
    if (isAiInterpretationFlag(row)) {
      return '-'
    }
    return row?.assignedInterpreterUserId || row?.interpreterUserId || '-'
  }

  const getInterpreterDisplay = (row: any): string => {
    if (isAiInterpretationFlag(row)) {
      return 'AI Interpreter'
    }

    if (isConsumer) {
      return getInterpreterIdDisplay(row)
    }
    return row?.assignedInterpreter || row?.interpreter || '-'
  }

  const isAiCall = (row?: any) => {
    if (isAiInterpretationFlag(row)) return true
    const code = String(row?.code ?? row?.callId ?? '')
    return /^AI-\d+/i.test(code)
  }

  // AI- prefix only when isAiInterpretation is 1, otherwise OPI-
  const getReferenceCallPrefix = (sourceRow?: any) => (isAiCall(sourceRow) ? 'AI' : 'OPI')

  const formatReferenceCallId = (code: number, sourceRow?: any) =>
    `${getReferenceCallPrefix(sourceRow)}-${String(code).padStart(6, '0')}`

  const getReferenceLabel = (sourceRow?: any) =>
    isAiCall(sourceRow) ? 'AI Reference ID' : 'OPI Reference ID'

  const getFkCallHeaderReference = (row?: any) => {
    const value =
      row?.fkCallHeaderReference ??
      row?.fK_CallHeaderReference ??
      row?.FK_CallHeaderReference ??
      null
    return value != null && Number(value) > 0 ? Number(value) : null
  }

  const findCallLogRowById = (code: number) =>
    call_log?.find((r: any) => Number(r?.id) === Number(code))

  const openReferenceCall = (fkCode: number, sourceRow?: any) => {
    const formattedId = formatReferenceCallId(fkCode, sourceRow)
    const targetRow = findCallLogRowById(fkCode) ?? {
      code: formattedId,
      id: fkCode,
      isAiInterpretation: getReferenceCallPrefix(sourceRow) === 'AI',
    }
    setActiveModalTab(1)
    fetchCallInDetails(formattedId, targetRow.initialContactId, targetRow)
  }

  const renderReferenceLink = (fkCode: number, sourceRow?: any) => (
    <div className='d-flex align-items-center mt-2'>
      <span className='text-gray-600 fw-normal fs-6 mb-0 me-2'>
        {getReferenceLabel(sourceRow)}:
      </span>
      <a
        href='#'
        className='text-primary fw-bold fs-6 text-hover-primary'
        onClick={(e) => {
          e.preventDefault()
          openReferenceCall(fkCode, sourceRow)
        }}
      >
        {formatReferenceCallId(fkCode, sourceRow)}
      </a>
    </div>
  )
  const {items: languagesList} = useSelector((state: RootState) => state.languages)

  const formatLanguageDisplay = useMemo(() => {
    const langMap = new Map<string, string>()
    ;(languagesList ?? []).forEach((l: {key?: string; value?: string}) => {
      const k = (l.key ?? '').toLowerCase()
      if (k) langMap.set(k, l.value ?? l.key ?? '')
    })
    return (raw: string | null | undefined): string => {
      if (!raw || String(raw).trim() === '') return '-'
      const s = String(raw).trim()
      const resolved = langMap.get(s.toLowerCase())
      return resolved ?? s
    }
  }, [languagesList])

  const formatChannelDisplay = (raw: string | null | undefined): string => {
    if (!raw || String(raw).trim() === '' || raw === '-') return '-'
    return String(raw).toUpperCase()
  }
  const [activeModalTab, setActiveModalTab] = useState(1)
  const [callDetails, setCallDetails] = useState<any>(null)
  const [routingDetails, setRoutingDetails] = useState<any>([])
  const [qaArray, setQaArray] = useState<any>([])
  const [note, setNote] = useState<any>('')
  const [isCallDetailsLoading, setIsCallDetailsLoading] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredData, setFilteredData] = useState(
    Array.isArray(routingDetails) ? routingDetails : []
  )
  const [interpreterRatting, setInterpreterRatting] = useState<any>(null)
  const [requesterRatting, setRequesterRatting] = useState<any>(null)
  const [audioTranscriptionData, setAudioTranscriptionData] = useState<any>(null)
  const [isLoadingAudioTranscription, setIsLoadingAudioTranscription] = useState(false)
  const [showEditInterpreterLangModal, setShowEditInterpreterLangModal] = useState(false)
  const [callDetailTags, setCallDetailTags] = useState<CallTag[]>([])

  const showCallTags =
    isSystemUser &&
    (title === 'OPI Logs' || title === 'On Demand Log')

  const getCallHeaderCodeFromDetails = (details?: any): number => {
    const fromId = Number(details?.id ?? 0)
    if (fromId > 0) return fromId
    const raw = String(details?.callId ?? '')
    const match = raw.match(/(\d+)/)
    return match ? Number(match[1]) : 0
  }

  const isScheduledCall = Number(callDetails?.appointmentId || 0) > 0

  // Hardcoded transcription data for testing
  const hardcodedTranscriptionData = {
    jobName: 'transcription-7-55c5488beef14f33a36b004a82222782',
    accountId: '595929585304',
    status: 'COMPLETED',
    results: {
      transcripts: [
        {
          transcript:
            "Hey, ma'am, how are you? I'm fine. What about you? Yes, I'm fine as well. So what's your mind today? Uh, basically, I need to work on transcription, um, and have to complete it today.",
        },
      ],
      speaker_labels: {
        segments: [
          {
            start_time: '1.009',
            end_time: '2.769',
            speaker_label: 'spk_0',
            items: [
              {speaker_label: 'spk_0', start_time: '2.21', end_time: '2.45'},
              {speaker_label: 'spk_0', start_time: '2.49', end_time: '2.769'},
            ],
          },
          {
            start_time: '2.849',
            end_time: '7.01',
            speaker_label: 'spk_1',
            items: [
              {speaker_label: 'spk_1', start_time: '2.849', end_time: '3.089'},
              {speaker_label: 'spk_1', start_time: '3.089', end_time: '3.21'},
              {speaker_label: 'spk_1', start_time: '3.21', end_time: '3.72'},
              {speaker_label: 'spk_1', start_time: '3.93', end_time: '4.269'},
              {speaker_label: 'spk_1', start_time: '4.269', end_time: '4.719'},
              {speaker_label: 'spk_1', start_time: '4.889', end_time: '5.21'},
              {speaker_label: 'spk_1', start_time: '5.21', end_time: '5.4'},
              {speaker_label: 'spk_1', start_time: '5.4', end_time: '6.039'},
              {speaker_label: 'spk_1', start_time: '6.849', end_time: '7.01'},
            ],
          },
          {
            start_time: '7.13',
            end_time: '7.769',
            speaker_label: 'spk_0',
            items: [
              {speaker_label: 'spk_0', start_time: '7.13', end_time: '7.369'},
              {speaker_label: 'spk_0', start_time: '7.369', end_time: '7.599'},
              {speaker_label: 'spk_0', start_time: '7.599', end_time: '7.769'},
            ],
          },
          {
            start_time: '7.769',
            end_time: '9.84',
            speaker_label: 'spk_1',
            items: [
              {speaker_label: 'spk_1', start_time: '7.769', end_time: '8.279'},
              {speaker_label: 'spk_1', start_time: '8.609', end_time: '8.97'},
              {speaker_label: 'spk_1', start_time: '8.97', end_time: '9.21'},
              {speaker_label: 'spk_1', start_time: '9.21', end_time: '9.489'},
              {speaker_label: 'spk_1', start_time: '9.489', end_time: '9.84'},
            ],
          },
          {
            start_time: '9.84',
            end_time: '10.479',
            speaker_label: 'spk_1',
            items: [{speaker_label: 'spk_1', start_time: '9.84', end_time: '10.479'}],
          },
          {
            start_time: '11.369',
            end_time: '18.68',
            speaker_label: 'spk_0',
            items: [
              {speaker_label: 'spk_0', start_time: '11.369', end_time: '11.72'},
              {speaker_label: 'spk_0', start_time: '11.729', end_time: '12.399'},
              {speaker_label: 'spk_0', start_time: '12.529', end_time: '12.97'},
              {speaker_label: 'spk_0', start_time: '12.97', end_time: '13.239'},
              {speaker_label: 'spk_0', start_time: '13.239', end_time: '13.489'},
              {speaker_label: 'spk_0', start_time: '13.489', end_time: '13.649'},
              {speaker_label: 'spk_0', start_time: '13.649', end_time: '13.97'},
              {speaker_label: 'spk_0', start_time: '13.97', end_time: '14.96'},
              {speaker_label: 'spk_0', start_time: '15.369', end_time: '15.96'},
              {speaker_label: 'spk_0', start_time: '16.809', end_time: '17.09'},
              {speaker_label: 'spk_0', start_time: '17.09', end_time: '17.329'},
              {speaker_label: 'spk_0', start_time: '17.329', end_time: '17.61'},
              {speaker_label: 'spk_0', start_time: '17.61', end_time: '17.77'},
              {speaker_label: 'spk_0', start_time: '17.77', end_time: '18.049'},
              {speaker_label: 'spk_0', start_time: '18.049', end_time: '18.479'},
            ],
          },
        ],
        channel_label: 'ch_0',
        speakers: 2,
      },
      items: [
        {
          id: 0,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'Hey'}],
          start_time: '2.21',
          end_time: '2.45',
          speaker_label: 'spk_0',
        },
        {
          id: 1,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_0',
        },
        {
          id: 2,
          type: 'pronunciation',
          alternatives: [{confidence: '0.992', content: "ma'am"}],
          start_time: '2.49',
          end_time: '2.769',
          speaker_label: 'spk_0',
        },
        {
          id: 3,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_0',
        },
        {
          id: 4,
          type: 'pronunciation',
          alternatives: [{confidence: '0.992', content: 'how'}],
          start_time: '2.849',
          end_time: '3.089',
          speaker_label: 'spk_1',
        },
        {
          id: 5,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'are'}],
          start_time: '3.089',
          end_time: '3.21',
          speaker_label: 'spk_1',
        },
        {
          id: 6,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'you'}],
          start_time: '3.21',
          end_time: '3.72',
          speaker_label: 'spk_1',
        },
        {
          id: 7,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: '?'}],
          speaker_label: 'spk_1',
        },
        {
          id: 8,
          type: 'pronunciation',
          alternatives: [{confidence: '0.89', content: "I'm"}],
          start_time: '3.93',
          end_time: '4.269',
          speaker_label: 'spk_1',
        },
        {
          id: 9,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'fine'}],
          start_time: '4.269',
          end_time: '4.719',
          speaker_label: 'spk_1',
        },
        {
          id: 10,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: '.'}],
          speaker_label: 'spk_1',
        },
        {
          id: 11,
          type: 'pronunciation',
          alternatives: [{confidence: '0.996', content: 'What'}],
          start_time: '4.889',
          end_time: '5.21',
          speaker_label: 'spk_1',
        },
        {
          id: 12,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'about'}],
          start_time: '5.21',
          end_time: '5.4',
          speaker_label: 'spk_1',
        },
        {
          id: 13,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'you'}],
          start_time: '5.4',
          end_time: '6.039',
          speaker_label: 'spk_1',
        },
        {
          id: 14,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: '?'}],
          speaker_label: 'spk_1',
        },
        {
          id: 15,
          type: 'pronunciation',
          alternatives: [{confidence: '0.997', content: 'Yes'}],
          start_time: '6.849',
          end_time: '7.01',
          speaker_label: 'spk_1',
        },
        {
          id: 16,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_1',
        },
        {
          id: 17,
          type: 'pronunciation',
          alternatives: [{confidence: '0.977', content: "I'm"}],
          start_time: '7.13',
          end_time: '7.369',
          speaker_label: 'spk_0',
        },
        {
          id: 18,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'fine'}],
          start_time: '7.369',
          end_time: '7.599',
          speaker_label: 'spk_0',
        },
        {
          id: 19,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'as'}],
          start_time: '7.599',
          end_time: '7.769',
          speaker_label: 'spk_0',
        },
        {
          id: 20,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'well'}],
          start_time: '7.769',
          end_time: '8.279',
          speaker_label: 'spk_1',
        },
        {
          id: 21,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: '.'}],
          speaker_label: 'spk_1',
        },
        {
          id: 22,
          type: 'pronunciation',
          alternatives: [{confidence: '0.996', content: 'So'}],
          start_time: '8.609',
          end_time: '8.97',
          speaker_label: 'spk_1',
        },
        {
          id: 23,
          type: 'pronunciation',
          alternatives: [{confidence: '0.992', content: "what's"}],
          start_time: '8.97',
          end_time: '9.21',
          speaker_label: 'spk_1',
        },
        {
          id: 24,
          type: 'pronunciation',
          alternatives: [{confidence: '0.997', content: 'your'}],
          start_time: '9.21',
          end_time: '9.489',
          speaker_label: 'spk_1',
        },
        {
          id: 25,
          type: 'pronunciation',
          alternatives: [{confidence: '0.996', content: 'mind'}],
          start_time: '9.489',
          end_time: '9.84',
          speaker_label: 'spk_1',
        },
        {
          id: 26,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'today'}],
          start_time: '9.84',
          end_time: '10.479',
          speaker_label: 'spk_1',
        },
        {
          id: 27,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: '?'}],
          speaker_label: 'spk_1',
        },
        {
          id: 28,
          type: 'pronunciation',
          alternatives: [{confidence: '0.987', content: 'Uh'}],
          start_time: '11.369',
          end_time: '11.72',
          speaker_label: 'spk_0',
        },
        {
          id: 29,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_0',
        },
        {
          id: 30,
          type: 'pronunciation',
          alternatives: [{confidence: '0.996', content: 'basically'}],
          start_time: '11.729',
          end_time: '12.399',
          speaker_label: 'spk_0',
        },
        {
          id: 31,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_0',
        },
        {
          id: 32,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'I'}],
          start_time: '12.529',
          end_time: '12.97',
          speaker_label: 'spk_0',
        },
        {
          id: 33,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'need'}],
          start_time: '12.97',
          end_time: '13.239',
          speaker_label: 'spk_0',
        },
        {
          id: 34,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'to'}],
          start_time: '13.239',
          end_time: '13.489',
          speaker_label: 'spk_0',
        },
        {
          id: 35,
          type: 'pronunciation',
          alternatives: [{confidence: '0.997', content: 'work'}],
          start_time: '13.489',
          end_time: '13.649',
          speaker_label: 'spk_0',
        },
        {
          id: 36,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'on'}],
          start_time: '13.649',
          end_time: '13.97',
          speaker_label: 'spk_0',
        },
        {
          id: 37,
          type: 'pronunciation',
          alternatives: [{confidence: '0.988', content: 'transcription'}],
          start_time: '13.97',
          end_time: '14.96',
          speaker_label: 'spk_0',
        },
        {
          id: 38,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_0',
        },
        {
          id: 39,
          type: 'pronunciation',
          alternatives: [{confidence: '0.789', content: 'um'}],
          start_time: '15.369',
          end_time: '15.96',
          speaker_label: 'spk_0',
        },
        {
          id: 40,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: ','}],
          speaker_label: 'spk_0',
        },
        {
          id: 41,
          type: 'pronunciation',
          alternatives: [{confidence: '0.996', content: 'and'}],
          start_time: '16.809',
          end_time: '17.09',
          speaker_label: 'spk_0',
        },
        {
          id: 42,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'have'}],
          start_time: '17.09',
          end_time: '17.329',
          speaker_label: 'spk_0',
        },
        {
          id: 43,
          type: 'pronunciation',
          alternatives: [{confidence: '0.999', content: 'to'}],
          start_time: '17.329',
          end_time: '17.61',
          speaker_label: 'spk_0',
        },
        {
          id: 44,
          type: 'pronunciation',
          alternatives: [{confidence: '0.997', content: 'complete'}],
          start_time: '17.61',
          end_time: '17.77',
          speaker_label: 'spk_0',
        },
        {
          id: 45,
          type: 'pronunciation',
          alternatives: [{confidence: '0.997', content: 'it'}],
          start_time: '17.77',
          end_time: '18.049',
          speaker_label: 'spk_0',
        },
        {
          id: 46,
          type: 'pronunciation',
          alternatives: [{confidence: '0.998', content: 'today'}],
          start_time: '18.049',
          end_time: '18.479',
          speaker_label: 'spk_0',
        },
        {
          id: 47,
          type: 'punctuation',
          alternatives: [{confidence: '0.0', content: '.'}],
          speaker_label: 'spk_0',
        },
      ],
      audio_segments: [
        {
          id: 0,
          transcript: "Hey, ma'am,",
          start_time: '1.009',
          end_time: '2.769',
          speaker_label: 'spk_0',
          items: [0, 1, 2, 3],
        },
        {
          id: 1,
          transcript: "how are you? I'm fine. What about you? Yes,",
          start_time: '2.849',
          end_time: '7.01',
          speaker_label: 'spk_1',
          items: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
        },
        {
          id: 2,
          transcript: "I'm fine as",
          start_time: '7.13',
          end_time: '7.769',
          speaker_label: 'spk_0',
          items: [17, 18, 19],
        },
        {
          id: 3,
          transcript: "well. So what's your mind",
          start_time: '7.769',
          end_time: '9.84',
          speaker_label: 'spk_1',
          items: [20, 21, 22, 23, 24, 25],
        },
        {
          id: 4,
          transcript: 'today?',
          start_time: '9.84',
          end_time: '10.479',
          speaker_label: 'spk_1',
          items: [26, 27],
        },
        {
          id: 5,
          transcript:
            'Uh, basically, I need to work on transcription, um, and have to complete it today.',
          start_time: '11.369',
          end_time: '18.68',
          speaker_label: 'spk_0',
          items: [28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47],
        },
      ],
    },
  }

  const getratting = async (logId: string | number) => {
    if (currentUser?.result?.userType === 'CONSUMER') {
      try {
        const response = await axios.get(`${API_URL}/AWSConnect/call-log-rating/${logId}`)
        setRequesterRatting(response.data.result)
        console.log('TESTER....', response.data.result)
        // setInterpreterRatting(response.data.result)
      } catch (error) {
        console.error('getratting error:', error)
      }
    }
    try {
      const response = await axios.get(
        `${API_URL}/AWSConnect/call-log-interpreter-rating/${logId.toString().split('-')[1]}`
      )
      setInterpreterRatting(response.data.result)
    } catch (error) {
      console.error('getratting error:', error)
    }
  }

  useEffect(() => {
    setActiveModalTab(1)
  }, [callDetails])

  // Transcription parsing functions (similar to TranscriptionDetail.tsx)
  interface TranscriptionLine {
    id: string
    timestamp: string
    speaker: string
    text: string
  }

  const parseAWSTranscribeFormat = (data: any): TranscriptionLine[] => {
    const lines: TranscriptionLine[] = []
    let lineNumber = 1

    if (data.results && data.results.audio_segments) {
      data.results.audio_segments.forEach((segment: any) => {
        // Convert speaker label (spk_0, spk_1) to Speaker A, Speaker B, etc.
        const speakerNumber = parseInt(segment.speaker_label.replace('spk_', ''))
        const speakerLabel = `Speaker ${String.fromCharCode(65 + speakerNumber)}` // A, B, C, etc.

        // Convert start_time from seconds to HH:MM:SS format
        const startTime = formatTimeFromSeconds(parseFloat(segment.start_time))

        // Split transcript into lines if it's long
        const words = segment.transcript.split(' ')
        const maxWordsPerLine = 15 // Adjust this number to control line length

        for (let i = 0; i < words.length; i += maxWordsPerLine) {
          const lineWords = words.slice(i, i + maxWordsPerLine)
          const lineText = lineWords.join(' ')

          lines.push({
            id: `line-${lineNumber}`,
            timestamp: startTime,
            speaker: speakerLabel,
            text: lineText,
          })

          lineNumber++
        }
      })
    }

    return lines
  }

  const formatTimeFromSeconds = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  const formatDuration = (seconds: number): string => {
    if (!seconds || seconds <= 0) return 'N/A'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    } else {
      return `${secs}s`
    }
  }

  // Format seconds to HH:mm:ss format (e.g., 02:32:22)
  const formatSecondsToHHMMSS = (seconds: number): string => {
    if (!seconds || seconds <= 0) return '00:00:00'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  const formatToBillingMinutes = (value: any): string => {
    const toMinutesLabel = (seconds: number): string => `${Math.round(seconds / 60)} min`
    if (value === null || value === undefined || value === '') return '-'

    if (typeof value === 'number' && Number.isFinite(value)) {
      return toMinutesLabel(value)
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed) return '-'

      const numericValue = Number(trimmed)
      if (Number.isFinite(numericValue)) {
        return toMinutesLabel(numericValue)
      }

      const hhmmssMatch = trimmed.match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/)
      if (hhmmssMatch) {
        const [, h, m, s] = hhmmssMatch
        const totalSeconds = Number(h) * 3600 + Number(m) * 60 + Number(s)
        return toMinutesLabel(totalSeconds)
      }
    }

    return String(value)
  }

  const renderTranscriptionDisplay = () => {
    let transcriptionData = null

    // Get transcription data from new API first
    if (audioTranscriptionData?.transcriptionDetail) {
      try {
        transcriptionData = JSON.parse(audioTranscriptionData.transcriptionDetail)
      } catch (e) {
        console.error('Error parsing transcriptionDetail:', e)
      }
    }
    // Fallback to old transcription data
    else if (callDetails?.transcriptionData?.transcriptionData) {
      try {
        transcriptionData = JSON.parse(callDetails.transcriptionData.transcriptionData)
      } catch (e) {
        console.error('Error parsing old transcription data:', e)
      }
    }

    if (!transcriptionData || !transcriptionData.results?.audio_segments) {
      return (
        <div
          className='bg-light p-4 rounded'
          style={{
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className='text-center text-muted'>
            <div className='d-flex justify-content-center mb-3'>
              <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-3x' />
            </div>
            <p className='mb-0'>No transcription available</p>
          </div>
        </div>
      )
    }

    // Parse and display transcription similar to TranscriptionDetail component
    const transcriptionLines = parseAWSTranscribeFormat(transcriptionData)

    return (
      <div className='transcription-content' style={{padding: '20px'}}>
        {transcriptionLines.map((line) => (
          <div
            key={line.id}
            className='transcription-line'
            style={{
              display: 'flex',
              marginBottom: '15px',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px',
            }}
          >
            <div
              className='line-number'
              style={{
                fontWeight: 'bold',
                color: '#666',
                minWidth: '50px',
                marginRight: '15px',
                fontFamily: 'Courier New, monospace',
              }}
            >
              {line.id.replace('line-', '').padStart(3, '0')}
            </div>
            <div className='line-content' style={{flex: 1}}>
              <div className='speaker-timestamp' style={{marginBottom: '5px'}}>
                <span
                  className='timestamp'
                  style={{
                    color: '#666',
                    fontFamily: 'Courier New, monospace',
                    marginRight: '10px',
                  }}
                >
                  [{line.timestamp}]
                </span>
                <span
                  className='speaker'
                  style={{
                    fontWeight: 'bold',
                    color: '#333',
                    marginRight: '10px',
                  }}
                >
                  {line.speaker}:
                </span>
              </div>
              <div
                className='transcription-text'
                style={{
                  color: '#333',
                  lineHeight: '1.5',
                }}
              >
                {line.text}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Function to get presigned URL from S3 (same as TranscriptionDetailPage.tsx)
  const getPresignedUrl = async (audioFileUrl: string): Promise<string | null> => {
    try {
      // Get JWT token from localStorage or your auth system
      const token = localStorage.getItem('accessToken')

      const response = await axios.post(
        `${API_URL}/Transcription/get-signed-url`,
        {
          audioFileUrl: audioFileUrl,
          expirationMinutes: 60,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.status !== 200) {
        throw new Error(`Failed to get presigned URL: ${response.status}`)
      }

      const data = response.data
      console.log('Presigned URL:', data.signedUrl)
      return data.signedUrl
    } catch (error) {
      console.error('Error getting presigned URL:', error)
      return null
    }
  }

  const handleDownloadAudio = async () => {
    try {
      let audioFileUrl = null

      // First try to get from audioTranscriptionData (new API)
      if (audioTranscriptionData?.audioFileUrl) {
        audioFileUrl = audioTranscriptionData.audioFileUrl
      }
      // Fallback to old recording data
      else if (callDetails?.recordingData?.hasRecording) {
        audioFileUrl =
          callDetails.recordingData.recordingUrl || callDetails.recordingData.originalUrl
      }

      if (!audioFileUrl) {
        toaster('warning', 'No recording available for this call')
        return
      }

      // Get presigned URL for S3 audio file
      const presignedUrl = await getPresignedUrl(audioFileUrl)

      if (!presignedUrl) {
        toaster('error', 'Failed to get download URL for audio file')
        return
      }

      // Create a temporary link to download the audio
      const link = document.createElement('a')
      link.href = presignedUrl
      // Extract file extension from URL or default to .wav
      const fileExtension = audioFileUrl.includes('.wav')
        ? 'wav'
        : audioFileUrl.includes('.mp3')
        ? 'mp3'
        : 'wav'
      link.download = `call-recording-${callDetails?.callId || 'unknown'}.${fileExtension}`
      link.target = '_blank' // Open in new tab as fallback
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toaster('success', 'Audio download started')
    } catch (error) {
      console.error('Error downloading audio:', error)
      toaster('error', 'Failed to download audio recording')
    }
  }

  const handleDownloadTranscriptionPDF = async () => {
    try {
      let transcriptionToUse = null

      // First try to get from audioTranscriptionData (new API)
      if (audioTranscriptionData?.transcriptionDetail) {
        try {
          transcriptionToUse = JSON.parse(audioTranscriptionData.transcriptionDetail)
        } catch (e) {
          console.error('Error parsing transcriptionDetail from new API:', e)
        }
      }

      // Fallback to old transcription data
      if (!transcriptionToUse && callDetails?.transcriptionData?.transcriptionData) {
        try {
          transcriptionToUse = JSON.parse(callDetails.transcriptionData.transcriptionData)
        } catch (e) {
          console.error('Error parsing transcription data:', e)
        }
      }

      // Last fallback to hardcoded data
      if (!transcriptionToUse) {
        transcriptionToUse = hardcodedTranscriptionData
      }

      if (!transcriptionToUse) {
        toaster('warning', 'No transcription data available to download')
        return
      }

      // Parse the transcription data
      const transcriptionLines = parseAWSTranscribeFormat(transcriptionToUse)

      // Show loading state
      const downloadBtn = document.querySelector(
        '.download-transcription-pdf-btn'
      ) as HTMLButtonElement
      if (downloadBtn) {
        downloadBtn.disabled = true
        downloadBtn.innerHTML =
          '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Generating PDF...'
      }

      // Create a temporary container for PDF generation
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '0'
      tempContainer.style.width = '800px'
      tempContainer.style.backgroundColor = 'white'
      tempContainer.style.padding = '40px'
      tempContainer.style.fontFamily = 'Arial, sans-serif'
      tempContainer.style.fontSize = '12px'
      tempContainer.style.lineHeight = '1.6'
      document.body.appendChild(tempContainer)

      // Create PDF header
      const header = document.createElement('div')
      header.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
          <h1 style="color: #333; margin: 0; font-size: 24px;">AdAstra</h1>
          <h2 style="color: #666; margin: 10px 0 0 0; font-size: 18px;">Machine Transcription Report</h2>
          <p style="color: #666; margin: 10px 0 0 0; font-size: 14px;">Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
      `
      tempContainer.appendChild(header)

      // Create transcription content for PDF
      const pdfContent = document.createElement('div')
      pdfContent.style.marginTop = '20px'

      transcriptionLines.forEach((line, index) => {
        const lineDiv = document.createElement('div')
        lineDiv.style.marginBottom = '15px'
        lineDiv.style.borderBottom = '1px solid #eee'
        lineDiv.style.paddingBottom = '10px'

        lineDiv.innerHTML = `
          <div style="display: flex; align-items: flex-start; margin-bottom: 5px;">
            <span style="font-weight: bold; color: #666; min-width: 50px; margin-right: 15px; font-family: 'Courier New', monospace;">
              ${(index + 1).toString().padStart(3, '0')}
            </span>
            <div style="flex: 1;">
              <div style="margin-bottom: 5px;">
                <span style="color: #666; font-family: 'Courier New', monospace; margin-right: 10px;">[${
                  line.timestamp
                }]</span>
                <span style="font-weight: bold; color: #333; margin-right: 10px;">${
                  line.speaker
                }:</span>
              </div>
              <div style="color: #333; line-height: 1.5;">
                ${line.text}
              </div>
            </div>
          </div>
        `
        pdfContent.appendChild(lineDiv)
      })

      tempContainer.appendChild(pdfContent)

      // Generate PDF using html2canvas and jsPDF
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      })

      // Remove temporary container
      document.body.removeChild(tempContainer)

      // Create PDF
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const imgWidth = 210 // A4 width in mm
      const pageHeight = 295 // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight

      let position = 0

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      // Download PDF
      pdf.save(`machine-transcription-${new Date().toISOString().split('T')[0]}.pdf`)

      // Reset button state
      if (downloadBtn) {
        downloadBtn.disabled = false
        downloadBtn.innerHTML =
          '<KTSVG path="/media/icons/duotune/files/fil021.svg" className="svg-icon-2 me-2" />PDF'
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again.')

      // Reset button state on error
      const downloadBtn = document.querySelector(
        '.download-transcription-pdf-btn'
      ) as HTMLButtonElement
      if (downloadBtn) {
        downloadBtn.disabled = false
        downloadBtn.innerHTML =
          '<KTSVG path="/media/icons/duotune/files/fil021.svg" className="svg-icon-2 me-2" />PDF'
      }
    }
  }

  const fetchRoutingDetails = async (contactId: string) => {
    if (!contactId || contactId === '-') return
    try {
      const routerResult = await axios.post(
        `${API_URL}/Dashboard/routing-details/${contactId}`
      )
      let routingData: any[] = []
      if (Array.isArray(routerResult?.data)) {
        routingData = routerResult.data
      } else if (routerResult?.data?.data && Array.isArray(routerResult.data.data)) {
        routingData = routerResult.data.data
      } else if (routerResult?.data?.Data && Array.isArray(routerResult.data.Data)) {
        routingData = routerResult.data.Data
      } else if (routerResult?.data?.result && Array.isArray(routerResult.data.result)) {
        routingData = routerResult.data.result
      }
      setRoutingDetails(routingData)
      setFilteredData(routingData)
    } catch (_) {}
  }

  const handleModalTabClick = async (tabNumber: React.SetStateAction<number>) => {
    if (!isSystemUser && tabNumber !== 1) return
    if (tabNumber == 4) {
      getratting(callDetails?.callId)
    }
    if (tabNumber == 3 && callDetails?.callId) {
      await fetchAudioTranscriptionData(callDetails.callId)
    }
    if (tabNumber == 2 && callDetails?.initialContactId) {
      await fetchRoutingDetails(callDetails.initialContactId)
    }
    setActiveModalTab(tabNumber)
  }

  const fetchAudioTranscriptionData = async (callLogId: string) => {
    setIsLoadingAudioTranscription(true)
    try {
      // Extract numeric ID from callLogId (e.g., "OPI-001335" -> "001335" or just use the code)
      const numericId =
        typeof callLogId === 'string' ? callLogId.match(/\d+/g)?.join('') ?? '' : callLogId
      const response = await axios.get(
        `${API_URL}/aws-connect/v4/call-header/${numericId}/audio-transcription`
      )

      if (response.data?.status === 'S' && response.data?.data) {
        setAudioTranscriptionData(response.data.data)
      } else {
        setAudioTranscriptionData(null)
        toaster('warning', 'No audio or transcription data available')
      }
    } catch (error: any) {
      console.error('Error fetching audio/transcription data:', error)
      setAudioTranscriptionData(null)
      if (error.response?.status !== 404) {
        toaster('error', 'Failed to fetch audio and transcription data')
      }
    } finally {
      setIsLoadingAudioTranscription(false)
    }
  }
  useEffect(() => {
    console.log('🔍 useEffect triggered - routingDetails:', routingDetails)
    console.log('🔍 routingDetails is array?', Array.isArray(routingDetails))
    console.log(
      '🔍 routingDetails length:',
      Array.isArray(routingDetails) ? routingDetails.length : 'Not array'
    )
    if (searchTerm === '') {
      const dataToSet = Array.isArray(routingDetails) ? routingDetails : []
      console.log('🔍 Setting filteredData to:', dataToSet)
      setFilteredData(dataToSet)
    }
  }, [searchTerm, routingDetails])

  const fetchCallInDetails = async (callId: string, initialContactId?: any, rowItem?: any) => {
    fetchCallIdRef.current = callId
    try {
      setIsCallDetailsLoading(true)
      const lookupId = initialContactId && initialContactId !== '-' ? initialContactId : callId

      let data: any = null
      try {
        const res = await axios.get(`${API_URL}/PreCalls/questions-transaction/${lookupId}`)
        data = res?.data?.data || null
      } catch (_) {}

      // Ignore response if user opened another row's details
      if (fetchCallIdRef.current !== callId) return

      // Prefer row item for display so modal matches the table (avoids API/view mismatch)
      const requesterFromRow = rowItem?.customer ?? data?.requester?.nameReqester
      const interpreterFromRow =
        rowItem?.interpreter ?? rowItem?.assignedInterpreter ?? data?.interpreter?.nameInterpreter
      const languageFromRow =
        rowItem?.languages ?? rowItem?.toLanguage ?? data?.requester?.language ?? data?.languages

      const fkCallHeaderReference = getFkCallHeaderReference(rowItem)

      const combinedData = {
        ...(data || {}),
        callId,
        initialContactId,
        fkCallHeaderReference,
        FK_CallHeaderReference: fkCallHeaderReference,
        requester:
          data?.requester != null
            ? {
                ...data.requester,
                nameReqester: requesterFromRow ?? data.requester.nameReqester,
                language: languageFromRow ?? data.requester.language,
              }
            : requesterFromRow || languageFromRow
              ? {nameReqester: requesterFromRow, language: languageFromRow}
              : undefined,
        interpreter:
          data?.interpreter != null
            ? {
                ...data.interpreter,
                nameInterpreter:
                  interpreterFromRow ?? data.interpreter.nameInterpreter,
              }
            : interpreterFromRow
              ? {nameInterpreter: interpreterFromRow}
              : undefined,
        interpreterId: rowItem?.interpreterId ?? '',
        appointmentId: rowItem?.appointmentId ?? 0,
        id: rowItem?.id ?? 0,
        accountCharge:
          typeof rowItem?.accountCharge === 'number'
            ? rowItem?.accountCharge
            : rowItem?.accountCharge
            ? parseFloat(rowItem?.accountCharge)
            : undefined,
        interpreterPay:
          typeof rowItem?.interpreterPay === 'number'
            ? rowItem?.interpreterPay
            : rowItem?.interpreterPay
            ? parseFloat(rowItem?.interpreterPay)
            : undefined,
        agentCallDuration: rowItem?.agentCallDuration,
        appointmentSessionStartedAt: rowItem?.appointmentSessionStartedAt,
        assignedInterpreter:
          rowItem?.assignedInterpreter ?? rowItem?.interpreter,
        assignedInterpreterId: rowItem?.assignedInterpreterId,
        assignedInterpreterUserId: rowItem?.assignedInterpreterUserId,
        interpreterUserId: rowItem?.interpreterUserId,
        isInvoiceCreated: rowItem?.isInvoiceCreated,
        isVoucherCreated: rowItem?.isVoucherCreated,
        disconnectTimestamp: rowItem?.disconnectTimestamp,
        connectedToAgentTimestamp: rowItem?.connectedToAgentTimestamp,
        totalCallDuration: rowItem?.totalCallDuration,
        companyName: rowItem?.companyName,
        interpreterNameInline: rowItem?.interpreter,
        languages: languageFromRow,
        initiationTimestamp: rowItem?.initiationTimestamp ?? data?.initiationTimestamp,
      }

      setCallDetails(combinedData)
      setCallDetailTags(
        Array.isArray(rowItem?.tags)
          ? rowItem.tags.map((t: any) => ({
              id: Number(t?.id ?? t?.Id ?? 0),
              name: String(t?.name ?? t?.Name ?? ''),
              color: String(t?.color ?? t?.Color ?? '#6B7280'),
              isSystem: Boolean(t?.isSystem ?? t?.IsSystem ?? false),
            }))
          : []
      )
      setQaArray(data?.qa ?? [])
      setNote(data?.note ?? '')
      setRoutingDetails([])
      setFilteredData([])
    } catch (error) {
      console.error(error)
    } finally {
      if (fetchCallIdRef.current === callId) {
        setIsCallDetailsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!externalCallOpen?.callId) return
    let cancelled = false
    ;(async () => {
      await fetchCallInDetails(
        externalCallOpen.callId,
        externalCallOpen.initialContactId,
        externalCallOpen.rowItem
      )
      if (cancelled) return
      const modalEl = document.getElementById('kt_call_in_details')
      if (modalEl) {
        Modal.getOrCreateInstance(modalEl).show()
      }
      onExternalCallOpened?.()
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCallOpen])

  const handleSaveAll = async () => {
    await updateCallInDetails(callDetails?.callId)
    hideModal()
  }

  const updateCallInDetails = async (callId: string) => {
    try {
      const result = await axios.put(`${API_URL}/PreCalls/questions-transaction`, {
        Model: qaArray.map((x: any) => {
          return {code: x?.code, answer: x?.answer}
        }),
        Note: note,
        Call_Id: callId,
      })
      if (result?.data?.status === 'S') {
        toaster('success', result?.data?.text)
        hideModal()
      } else {
        toaster('success', result?.data?.text)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const hideModal = () => {
    const modal = document.getElementById('kt_call_in_details')
    if (modal) {
      const modalInstance = Modal.getInstance(modal)
      if (modalInstance) modalInstance.hide()
    }
    setActiveModalTab(1)
  }

  const renderQuestionAnswerPairs = (questions: any, answers: any) => {
    const questionList = questions
      ? questions.split('|').map((q: string) => q.trim())
      : []
    const answerList = answers
      ? answers.split('|').map((a: string) => a.trim())
      : []
    const maxLen = Math.max(questionList.length, answerList.length)
    const seen = new Set<string>()
    const pairs: {question: string; answer: string}[] = []
    for (let index = 0; index < maxLen; index += 1) {
      const question = questionList[index] || ''
      const answer = answerList[index] || ''
      if (!question && !answer) continue
      const key = `${question}||${answer}`
      if (seen.has(key)) continue
      seen.add(key)
      pairs.push({question, answer})
    }

    if (pairs.length === 0) {
      return '-'
    }

    return (
      <table className='w-100 mb-0'>
        <tbody>
          {pairs.map((pair, index) => (
            <tr key={`${pair.question}-${index}`}>
              <td className='text-start align-top border-0 py-1 pe-3 ps-0' style={{width: '50%'}}>
                {pair.question ? (
                  <span className='d-block'>
                    <span className='fw-semibold me-1'>Q.</span>
                    {pair.question}
                  </span>
                ) : null}
              </td>
              <td className='text-start align-top border-0 py-1 pe-0 ps-0' style={{width: '50%'}}>
                {pair.answer ? (
                  <span className='d-block'>
                    <span className='me-1'>➤</span>
                    {pair.answer}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  const copyToClipboard = (callId: string) => {
    if (callId) {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(callId)
          .then(() => {
            setTooltipVisible(true)
            setTimeout(() => {
              setTooltipVisible(false)
            }, 2000)
          })
          .catch((err) => {
            console.error('Failed to copy: ', err)
          })
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = callId
        document.body.appendChild(textarea)
        textarea.select()
        try {
          document.execCommand('copy')
          setTooltipVisible(true)
          setTimeout(() => {
            setTooltipVisible(false)
          }, 2000)
        } catch (err) {
          console.error('Fallback: Failed to copy: ', err)
        }
        document.body.removeChild(textarea)
      }
    }
  }

  const handleSearch = () => {
    const routingArray = Array.isArray(routingDetails) ? routingDetails : []
    if (searchTerm.trim() === '') {
      setFilteredData(routingArray)
    } else {
      const filtered = routingArray.filter((item: any) =>
        item.interpereterName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredData(filtered)
    }
  }
  console.log(callDetails, 'callDetails with recording and transcription data')

  const renderStar = (filledCount: number, maxStars = 5): JSX.Element => {
    const stars = Array.from({length: maxStars}, (_, i) => {
      const filled = i < Math.max(0, Math.min(filledCount, maxStars))
      return (
        <span
          key={i}
          className={`fs-2 me-1 ${filled ? 'text-warning' : 'text-muted'}`}
          aria-hidden='true'
        >
          {filled ? '★' : '☆'}
        </span>
      )
    })

    return <div className='d-flex align-items-center'>{stars}</div>
  }

  return (
    <div className={hideTable ? className : `card ${className}`}>
      <div className='modal fade' tabIndex={-1} id='kt_call_in_details'>
        <div className='modal-dialog modal-xl'>
          <div className='modal-content'>
            <div className='modal-header py-2'>
              <h4 className='modal-title'>Details</h4>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                data-bs-dismiss='modal'
                aria-label='Close'
                onClick={() => hideModal()}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>
            <div className='modal-body'>
              {!isCallDetailsLoading && (
                <>
                  <div className='d-flex align-items-center justify-content-start flex-wrap'>
                    <h5 className='d-flex text-gray-600 fw-normal fs-2 mb-0'>
                      Call ID : {callDetails?.callId}
                    </h5>{' '}
                    <span
                      className='d-flex ms-3'
                      onClick={() => copyToClipboard(callDetails?.callId)}
                      style={{cursor: 'pointer'}}
                      ref={target}
                    >
                      <KTSVG
                        path='media/icons/duotune/general/gen054.svg'
                        className='svg-icon-muted svg-icon-2'
                      />
                    </span>
                    <Overlay target={target.current} show={tooltipVisible} placement='top'>
                      {(props) => (
                        <Tooltip id='overlay-tooltip' {...props}>
                          Call ID copied!
                        </Tooltip>
                      )}
                    </Overlay>
                    {showCallTags && <CallTagBadges tags={callDetailTags} />}
                  </div>
                  {getFkCallHeaderReference(callDetails) &&
                    renderReferenceLink(getFkCallHeaderReference(callDetails)!, callDetails)}
                  <div className='row g-4'>
                    <div className='mb-5'>
                      <div
                        className='py-3'
                        style={{minWidth: '300px', overflowX: 'auto', minHeight: '50px'}}
                      >
                        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-semibold flex-nowrap'>
                          <li className='nav-item'>
                            <a
                              className={`nav-link fs-7 text-gray-400   ${
                                activeModalTab === 1 ? 'text-active-dark fw-bold active show' : ''
                              }`}
                              data-bs-toggle='tab'
                              href='#kt_modaltab_pane_1'
                              onClick={() => handleModalTabClick(1)}
                            >
                              Call Details
                            </a>
                          </li>
                          {isSystemUser && (
                            <>
                              <li className='nav-item'>
                                <a
                                  className={`nav-link fs-7 text-gray-400 ${
                                    activeModalTab === 2
                                      ? 'text-active-dark fw-bold active show'
                                      : ''
                                  }`}
                                  data-bs-toggle='tab'
                                  href='#kt_modaltab_pane_2'
                                  onClick={() => handleModalTabClick(2)}
                                >
                                  Routing Details
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  className={`nav-link fs-7 text-gray-400 ${
                                    activeModalTab === 3
                                      ? 'text-active-dark fw-bold active show'
                                      : ''
                                  }`}
                                  data-bs-toggle='tab'
                                  href='#kt_modaltab_pane_3'
                                  onClick={() => handleModalTabClick(3)}
                                >
                                  Audio & Transcription
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  className={`nav-link fs-7 text-gray-400 ${
                                    activeModalTab === 4
                                      ? 'text-active-dark fw-bold active show'
                                      : ''
                                  }`}
                                  data-bs-toggle='tab'
                                  href='#kt_modaltab_pane_4'
                                  onClick={() => handleModalTabClick(4)}
                                >
                                  Feedback & Rating
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  className={`nav-link fs-7 text-gray-400 ${
                                    activeModalTab === 5
                                      ? 'text-active-dark fw-bold active show'
                                      : ''
                                  }`}
                                  data-bs-toggle='tab'
                                  href='#kt_modaltab_pane_5'
                                  onClick={() => handleModalTabClick(5)}
                                >
                                  Billing
                                </a>
                              </li>
                              {
                                <li className='nav-item'>
                                  <a
                                    className={`nav-link fs-7 text-gray-400 ${
                                      activeModalTab === 6
                                        ? 'text-active-dark fw-bold active show'
                                        : ''
                                    }`}
                                    data-bs-toggle='tab'
                                    href='#kt_modaltab_pane_6'
                                    onClick={() => handleModalTabClick(6)}
                                  >
                                    Activities
                                  </a>
                                </li>
                              }
                            </>
                          )}
                        </ul>
                      </div>
                      <div className=''>
                        <div className='my-3'>
                          <div className='tab-content' id='myTabContent'>
                            {activeModalTab === 1 && (
                              <div className=''>
                                <div
                                  className='tab-pane fade show active'
                                  id='kt_modaltab_pane_1'
                                  role='tabpanel'
                                >
                                  <>
                                    <div className=''>
                                      {/* Call Metadata Section */}
                                      <div className='mb-5'>
                                        <div className='d-flex align-items-center justify-content-between mb-4'>
                                          <h5 className='fs-4 fw-bold mb-0'>Call Metadata</h5>
                                          {currentUser?.result.userType === 'SYSTEM' && (
                                            <button
                                              type='button'
                                              className='btn btn-sm btn-icon btn-light'
                                              onClick={() => setShowEditInterpreterLangModal(true)}
                                            >
                                              <KTSVG
                                                path='/media/icons/duotune/art/art005.svg'
                                                className='svg-icon-3'
                                              />
                                            </button>
                                          )}
                                        </div>
                                        <EditInterpreterLanguageModal
                                          show={showEditInterpreterLangModal}
                                          onClose={() => setShowEditInterpreterLangModal(false)}
                                          canEdit={currentUser?.result.userType === 'SYSTEM'}
                                          callDetails={callDetails}
                                          onRefresh={onRefresh}
                                          onSaved={(next) => {
                                            setCallDetails((prev: any) => {
                                              if (!prev) return prev
                                              return {
                                                ...prev,
                                                interpreterId:
                                                  next?.interpreterUserId ?? prev.interpreterId,
                                                assignedInterpreterId:
                                                  next?.interpreterUserId ??
                                                  prev.assignedInterpreterId,
                                                assignedInterpreter:
                                                  next?.interpreterLabel ??
                                                  prev.assignedInterpreter,
                                                interpreterNameInline:
                                                  next?.interpreterLabel ??
                                                  prev.interpreterNameInline,
                                                languages: next?.languageLabel ?? prev.languages,
                                                requester: {
                                                  ...(prev.requester || {}),
                                                  language:
                                                    next?.languageLabel ??
                                                    prev?.requester?.language,
                                                },
                                                connectedToAgentTimestamp:
                                                  next?.startTimeIso ??
                                                  prev.connectedToAgentTimestamp,
                                                initiationTimestamp: prev.initiationTimestamp,
                                                disconnectTimestamp:
                                                  next?.endTimeIso ?? prev.disconnectTimestamp,
                                              }
                                            })
                                          }}
                                        />
                                        {/* Client & Service */}
                                        <div className='mb-4 p-4 rounded border border-gray-300 bg-secondary bg-opacity-10'>
                                          <h6 className='text-muted text-uppercase fs-8 fw-bold ls-1 mb-3'>
                                            Client &amp; Service
                                          </h6>
                                          <div
                                            className='row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3'
                                          >
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Client Name
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.companyName || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Service Type | Gender Preference
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {`${callDetails?.requester?.serviceType || 'N/A'} | ${
                                                    callDetails?.requester?.genderPreference ||
                                                    callDetails?.genderPreference ||
                                                    'N/A'
                                                  }`}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Communication Type
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.communicationType || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Language
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.requester?.language ||
                                                    callDetails?.languages ||
                                                    'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Participants */}
                                        <div className='mb-4 p-4 rounded border border-gray-300 bg-secondary bg-opacity-10'>
                                          <h6 className='text-muted text-uppercase fs-8 fw-bold ls-1 mb-3'>
                                            Participants
                                          </h6>
                                          <div
                                            className='row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3'
                                          >
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Requester Name
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.requester?.nameReqester || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            {!isConsumer && (
                                              <div className='col min-w-0'>
                                                <div className='d-flex flex-column'>
                                                  <span className='text-muted fs-7 mb-1'>
                                                    Assigned Interpreter
                                                  </span>
                                                  <span className='fw-bold text-break'>
                                                    {callDetails?.assignedInterpreter || 'N/A'}
                                                  </span>
                                                </div>
                                              </div>
                                            )}
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Interpreter ID
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {getInterpreterIdDisplay(callDetails) === '-'
                                                    ? 'N/A'
                                                    : getInterpreterIdDisplay(callDetails)}
                                                </span>
                                              </div>
                                            </div>
                                            {!isConsumer && (
                                              <div className='col min-w-0'>
                                                <div className='d-flex flex-column'>
                                                  <span className='text-muted fs-7 mb-1'>
                                                    Initially Picked By
                                                  </span>
                                                  <span className='fw-bold text-break'>
                                                    {callDetails?.interpreter?.nameInterpreter ||
                                                      callDetails?.interpreterNameInline ||
                                                      'N/A'}
                                                  </span>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Timing */}
                                        <div className='mb-4 p-4 rounded border border-gray-300 bg-secondary bg-opacity-10'>
                                          <h6 className='text-muted text-uppercase fs-8 fw-bold ls-1 mb-3'>
                                            Timing
                                          </h6>
                                          <div
                                            className='row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3'
                                          >
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>Date</span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.initiationTimestamp
                                                    ? new Date(
                                                        callDetails.initiationTimestamp
                                                      ).toLocaleDateString()
                                                    : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Start Time
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.connectedToAgentTimestamp &&
                                                  callDetails.connectedToAgentTimestamp !==
                                                    'N/A' &&
                                                  !isNaN(
                                                    new Date(
                                                      callDetails.connectedToAgentTimestamp
                                                    ).getTime()
                                                  )
                                                    ? new Date(
                                                        callDetails.connectedToAgentTimestamp
                                                      ).toLocaleTimeString()
                                                    : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  End Time
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.disconnectTimestamp
                                                    ? new Date(
                                                        callDetails.disconnectTimestamp
                                                      ).toLocaleTimeString()
                                                    : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Session Active Time
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.appointmentSessionStartedAt
                                                    ? new Date(
                                                        callDetails?.appointmentSessionStartedAt
                                                      ).toLocaleTimeString()
                                                    : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Duration
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.agentCallDuration
                                                    ? callDetails.agentCallDuration
                                                    : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Identifiers */}
                                        <div className='mb-2 p-4 rounded border border-gray-300 bg-secondary bg-opacity-10'>
                                          <h6 className='text-muted text-uppercase fs-8 fw-bold ls-1 mb-3'>
                                            Identifiers
                                          </h6>
                                          <div
                                            className='row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3'
                                          >
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Call ID
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.callId || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            {getFkCallHeaderReference(callDetails) && (
                                              <div className='col min-w-0'>
                                                <div className='d-flex flex-column'>
                                                  <span className='text-muted fs-7 mb-1'>
                                                    {getReferenceLabel(callDetails)}
                                                  </span>
                                                  <a
                                                    href='#'
                                                    className='fw-bold text-primary text-hover-primary text-break'
                                                    onClick={(e) => {
                                                      e.preventDefault()
                                                      openReferenceCall(
                                                        getFkCallHeaderReference(callDetails)!,
                                                        callDetails
                                                      )
                                                    }}
                                                  >
                                                    {formatReferenceCallId(
                                                      getFkCallHeaderReference(callDetails)!,
                                                      callDetails
                                                    )}
                                                  </a>
                                                </div>
                                              </div>
                                            )}
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Assignment ID
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.appointmentId || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                            <div className='col min-w-0'>
                                              <div className='d-flex flex-column'>
                                                <span className='text-muted fs-7 mb-1'>
                                                  Meeting Code
                                                </span>
                                                <span className='fw-bold text-break'>
                                                  {callDetails?.meetingCode || 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {showCallTags && (
                                          <CallTagsSection
                                            callHeaderCode={getCallHeaderCodeFromDetails(callDetails)}
                                            enabled={showCallTags}
                                            onTagsChange={setCallDetailTags}
                                          />
                                        )}
                                      </div>

                                      {/* Pre-Call Questions and Answers Section */}
                                      <div className='separator my-3'></div>
                                      <div className='mb-5'>
                                        <div className='d-flex justify-content-between align-items-center mb-4'>
                                          <h5 className='fs-4 fw-bold mb-0'>
                                            Pre-Call Questions and Answers
                                          </h5>
                                          <button className='btn btn-sm btn-light-primary'>
                                            <KTSVG
                                              path='/media/icons/duotune/files/fil021.svg'
                                              className='svg-icon-2 me-2'
                                            />
                                            Download
                                          </button>
                                        </div>
                                      </div>
                                      {qaArray?.length > 0 && (
                                        <>
                                          <div className='separator my-3'></div>
                                          <div className='row'>
                                            {currentUser?.result.userType === 'SYSTEM' ? (
                                              <h6 className='fs-7 fw-normal py-5'>
                                                You can edit any field that was answered on this
                                                call. New fields added to a pre-call policy will not
                                                be included below.
                                              </h6>
                                            ) : (
                                              <h6 className='fs-7 fw-normal py-5'>
                                                Pre-call policy questions and answers
                                              </h6>
                                            )}
                                          </div>
                                          <div
                                            className='d-flex flex-column gap-3'
                                            style={{maxHeight: '400px', overflowY: 'auto'}}
                                          >
                                            {qaArray?.map((qa: any, index: number) => (
                                              <div
                                                key={qa?.code ?? index}
                                                className='d-flex align-items-center gap-3'
                                              >
                                                <label
                                                  htmlFor={`precall-answer-${qa?.code ?? index}`}
                                                  className='form-label fs-7 fw-bold mb-0 text-gray-800 flex-shrink-0'
                                                  style={{minWidth: '40%', maxWidth: '50%'}}
                                                >
                                                  {index + 1}. {qa?.question ?? ''}
                                                </label>
                                                {currentUser?.result.userType === 'SYSTEM' ? (
                                                  <input
                                                    id={`precall-answer-${qa?.code ?? index}`}
                                                    type='text'
                                                    className='form-control form-control-sm form-control-white flex-grow-1'
                                                    placeholder='Enter Answer'
                                                    value={qa?.answer ?? ''}
                                                    onChange={(e: any) => {
                                                      const updatedItem = {
                                                        code: qa.code,
                                                        answer:
                                                          e.target.value !== ''
                                                            ? e.target.value
                                                            : '',
                                                        question: qa.question,
                                                      }
                                                      const itemIndex = qaArray.findIndex(
                                                        (i: any) => i.code === qa.code
                                                      )

                                                      if (itemIndex !== -1) {
                                                        const newQaArray = [...qaArray]
                                                        newQaArray[itemIndex] = updatedItem
                                                        setQaArray(newQaArray)
                                                      }
                                                    }}
                                                  />
                                                ) : (
                                                  <span className='fs-7 text-gray-700 flex-grow-1'>
                                                    {qa?.answer ?? 'N/A'}
                                                  </span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                    {/* qaArray?.length > 0 && */}

                                    {currentUser?.result.userType === 'SYSTEM' && (
                                      <div className='text-end pt-3 mt-3'>
                                        <button
                                          type='reset'
                                          className='btn btn-sm btn-light me-2'
                                          onClick={() => hideModal()}
                                        >
                                          Cancel
                                        </button>

                                        <button
                                          type='submit'
                                          className='btn btn-sm btn-primary'
                                          data-kt-menu-dismiss='true'
                                          onClick={handleSaveAll}
                                        >
                                          Save
                                        </button>
                                      </div>
                                    )}
                                  </>
                                </div>
                              </div>
                            )}
                            {isSystemUser && activeModalTab === 3 && (
                              <div className=''>
                                <div
                                  className='tab-pane fade show active'
                                  id='kt_modaltab_pane_3'
                                  role='tabpanel'
                                >
                                  <div className='mb-5'>
                                    <h5 className='fs-4 fw-bold mb-4'>Audio & Transcription</h5>

                                    {/* Audio Recording Section */}
                                    <div className='card mb-4'>
                                      <div className='card-body'>
                                        <div className=' align-items-center mb-3'></div>
                                        <div
                                          className='bg-light p-4 rounded'
                                          style={{minHeight: '100px'}}
                                        >
                                          <div className='text-center text-muted'>
                                            <h6 className='fw-bold text-center mb-4'>
                                              Audio Recording
                                            </h6>
                                            <KTSVG
                                              path='/media/icons/duotune/media/med001.svg'
                                              className='svg-icon-3x mb-3'
                                            />
                                            <button
                                              className='btn btn-primary btn-sm'
                                              onClick={handleDownloadAudio}
                                              disabled={
                                                !audioTranscriptionData?.audioFileUrl &&
                                                !callDetails?.recordingData?.hasRecording
                                              }
                                            >
                                              <KTSVG
                                                path='/media/icons/duotune/files/fil021.svg'
                                                className='svg-icon-2 me-2'
                                              />
                                              {audioTranscriptionData?.audioFileUrl ||
                                              callDetails?.recordingData?.hasRecording
                                                ? 'Download Recording'
                                                : 'No Recording Available'}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Machine Transcript Section */}
                                    <div className='card'>
                                      <div className='card-body'>
                                        <div className='bg-light p-4 rounded d-flex justify-content-between align-items-center mb-3'>
                                          <h6 className='fw-bold mb-0'>Machine Transcript</h6>
                                          <button
                                            className='btn btn-primary btn-sm download-transcription-pdf-btn'
                                            onClick={handleDownloadTranscriptionPDF}
                                            disabled={
                                              !audioTranscriptionData?.transcriptionDetail &&
                                              !callDetails?.transcriptionData?.transcriptionData
                                            }
                                          >
                                            <KTSVG
                                              path='/media/icons/duotune/files/fil021.svg'
                                              className='svg-icon-2 me-2'
                                            />
                                            PDF
                                          </button>
                                        </div>
                                        {isLoadingAudioTranscription ? (
                                          <div className='text-center py-5'>
                                            <CommonLoading />
                                          </div>
                                        ) : (
                                          <div
                                            className='transcription-display-container'
                                            style={{maxHeight: '400px', overflowY: 'auto'}}
                                          >
                                            {renderTranscriptionDisplay()}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {isSystemUser && activeModalTab === 4 && (
                              <div className=''>
                                <div
                                  className='tab-pane fade show active'
                                  id='kt_modaltab_pane_4'
                                  role='tabpanel'
                                >
                                  <div className='mb-5'>
                                    <h5 className='fs-4 fw-bold mb-4'>Feedback & Rating</h5>

                                    {/* Requester Feedback */}
                                    {requesterRatting?.hasRating && (
                                      <div className='card mb-4'>
                                        <div className='card-body'>
                                          <h6 className='fw-bold mb-3'>
                                            {requesterRatting
                                              ? requesterRatting?.requesterName
                                              : ''}{' '}
                                            - Feedback
                                          </h6>
                                          <div className='mb-3'>
                                            {renderStar(requesterRatting?.rating?.callRating)}
                                          </div>
                                          <div className='form-group'>
                                            <textarea
                                              readOnly={true}
                                              className='form-control'
                                              rows={3}
                                              placeholder='No Comment Provided'
                                              value={requesterRatting?.rating?.callComment || ''}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Interpreter Feedback */}
                                    {interpreterRatting?.hasRating && (
                                      <div className='card'>
                                        <div className='card-body'>
                                          <h6 className='fw-bold mb-3'>
                                            {interpreterRatting
                                              ? interpreterRatting?.interpreterName
                                              : ''}{' '}
                                            - Feedback
                                          </h6>
                                          <div className='mb-3'>
                                            {/* {renderStarRating(interpreterRating, setInterpreterRating)} */}
                                            {renderStar(interpreterRatting?.rating?.rating)}
                                          </div>
                                          <div className='form-group'>
                                            <textarea
                                              className='form-control'
                                              rows={3}
                                              placeholder='No Comment Provided'
                                              readOnly={true}
                                              value={interpreterRatting?.rating?.comment || ''}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {!interpreterRatting?.hasRating &&
                                    !requesterRatting?.hasRating ? (
                                      <div className='text-muted fs-5 '>No Feedback Available</div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            )}
                            {isSystemUser && activeModalTab === 5 && (
                              <div className=''>
                                <div
                                  className='tab-pane fade show active'
                                  id='kt_modaltab_pane_5'
                                  role='tabpanel'
                                >
                                  <div className='mb-5'>
                                    <h5 className='fs-4 fw-bold mb-4'>Billing</h5>
                                    <CallBillingTab callDetails={callDetails} />
                                  </div>
                                </div>
                              </div>
                            )}
                            {isSystemUser && activeModalTab === 6 && (
                              <div className=''>
                                <div
                                  className='tab-pane fade show active'
                                  id='kt_modaltab_pane_6'
                                  role='tabpanel'
                                >
                                  <ScheduledCallActivitiesTab
                                    appointmentId={Number(callDetails?.appointmentId || 0)}
                                    callLogHeaderId={Number(callDetails?.id || 0)}
                                  />
                                </div>
                              </div>
                            )}
                            {isSystemUser && activeModalTab === 2 && (
                              <div className=''>
                                <div
                                  className='tab-pane fade show active'
                                  id='kt_modaltab_pane_2'
                                  role='tabpanel'
                                >
                                  <>
                                    <div className='separator my-3'></div>
                                    <div className='row'>
                                      <h6 className='fs-6 fw-normal py-5'>
                                        The list below shows all interpreters in your roster that
                                        were dialed before this call was picked up or abandoned.
                                      </h6>
                                    </div>
                                    <div className='d-flex flex-wrap flex-end'>
                                      <div className='d-flex align-items-center me-3 flex-nowrap'>
                                        <input
                                          type='text'
                                          className='form-control form-control-white form-control-sm max-w-250px custom-search-radius'
                                          placeholder='Search by name'
                                          value={searchTerm}
                                          onChange={(e) => setSearchTerm(e.target.value)}
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              handleSearch()
                                            }
                                          }}
                                        />

                                        <button
                                          type='button'
                                          className='btn btn-primary btn-icon btn-sm custom-search-btn-radius px-3'
                                          onClick={handleSearch}
                                        >
                                          <KTSVG
                                            path='/media/icons/duotune/general/gen021.svg'
                                            className=''
                                          />
                                        </button>
                                      </div>
                                    </div>
                                    <div className='table-responsive'>
                                      {/* begin::Table */}
                                      <table className='table table-row-dashed table-row-gray-300 align-middle gs-3 gy-3'>
                                        {/* begin::Table head */}
                                        <thead>
                                          <tr className='fw-semibold text-muted text-uppercase'>
                                            <th className='min-w-100px'>Interpreter Name</th>
                                            <th className='min-w-150px'>Time Dialed</th>
                                            <th className='min-w-150px'>Ring Duration</th>
                                            <th className='min-w-150px'>Device</th>
                                            <th className='min-w-150px'>Response Status</th>
                                            <th className='min-w-150px'>Notification Received</th>
                                            <th className='min-w-150px text-end'>Call Status</th>
                                          </tr>
                                        </thead>
                                        {/* end::Table head */}
                                        {/* begin::Table body */}
                                        <tbody>
                                          {(() => {
                                            console.log(
                                              '🔍 Rendering table - filteredData:',
                                              filteredData
                                            )
                                            console.log(
                                              '🔍 filteredData length:',
                                              filteredData?.length
                                            )
                                            return null
                                          })()}
                                          {filteredData?.length > 0 ? (
                                            filteredData.map(
                                              (
                                                item: {
                                                  interpereterAvatar: string | null
                                                  interpereterName: string
                                                  awsId: string
                                                  routingTime: string | number | Date
                                                  ringDuration: number
                                                  device: string
                                                  isConnected: boolean
                                                  connectionFailed?: boolean | number | null
                                                  responseStatus: string
                                                  respondedAt: string | null
                                                  invitedAt: string
                                                  notificationReceived: boolean | null | undefined
                                                },
                                                index: React.Key | null | undefined
                                              ) => (
                                                <tr key={item.awsId || item.invitedAt || index}>
                                                  <td>
                                                    <div className='d-flex flex-wrap align-items-center'>
                                                      <div className='symbol-hover me-3'>
                                                        <div className='symbol symbol-circle symbol-50px overflow-hidden'>
                                                          {item.interpereterAvatar ? (
                                                            <div className='symbol-label'>
                                                              <ProfileImg
                                                                imagePath={item.interpereterAvatar}
                                                                alt={
                                                                  item.interpereterName
                                                                    ?.charAt(0)
                                                                    .toUpperCase() || ''
                                                                }
                                                                className='w-100'
                                                              />
                                                            </div>
                                                          ) : (
                                                            <div
                                                              className={clsx(
                                                                'symbol-label fs-4',
                                                                'badge-light-success',
                                                                'bg-light-success'
                                                              )}
                                                            >
                                                              <span style={{fontSize: '20x'}}>
                                                                {(item.interpereterName ?? '')
                                                                  .charAt(0)
                                                                  .toUpperCase() +
                                                                  (item.interpereterName ?? '')
                                                                    .charAt(1)
                                                                    .toUpperCase()}
                                                              </span>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                      <div className='d-flex flex-column'>
                                                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                                                          {item.interpereterName}
                                                        </a>
                                                      </div>
                                                    </div>
                                                  </td>
                                                  <td>
                                                    <a className='text-gray-800 text-hover-primary d-block fs-6'>
                                                      {item.routingTime
                                                        ? new Date(
                                                            item.routingTime
                                                          ).toLocaleString()
                                                        : 'N/A'}
                                                    </a>
                                                  </td>
                                                  <td>
                                                    <a className='text-gray-800 text-hover-primary d-block fs-6'>
                                                      {item.ringDuration != null && item.ringDuration >= 0
                                                        ? formatSecondsToHHMMSS(item.ringDuration)
                                                        : '-'}
                                                    </a>
                                                  </td>
                                                  <td>
                                                    <a className='text-gray-800 text-hover-primary d-block fs-6'>
                                                      {item.device || 'N/A'}
                                                    </a>
                                                  </td>
                                                  <td>
                                                    <a className='text-gray-800 text-hover-primary d-block fs-6'>
                                                      {item.responseStatus ? (
                                                        <span
                                                          className={`badge ${
                                                            item.responseStatus === 'Accepted'
                                                              ? 'badge-light-success'
                                                              : item.responseStatus === 'Rejected'
                                                              ? 'badge-light-danger'
                                                              : 'badge-light-warning'
                                                          } px-2 py-1`}
                                                        >
                                                          {item.responseStatus === 'Pending'
                                                            ? 'No answer'
                                                            : item.responseStatus}
                                                        </span>
                                                      ) : (
                                                        'N/A'
                                                      )}
                                                    </a>
                                                  </td>
                                                  <td>
                                                    <a className='text-gray-800 text-hover-primary d-block fs-6'>
                                                      {item.notificationReceived === true
                                                        ? 'Yes'
                                                        : item.notificationReceived === false
                                                        ? 'No'
                                                        : 'N/A'}
                                                    </a>
                                                  </td>
                                                  <td className='text-end'>
                                                    <div className='d-flex flex-column align-items-end'>
                                                      {item.connectionFailed === true ||
                                                      item.connectionFailed === 1 ? (
                                                        <span
                                                          className='badge px-3 py-2 fs-9 d-flex justify-content-center'
                                                          style={{width: '140px', backgroundColor: '#FFF9E6', color: '#fda11a'}}
                                                        >
                                                          Connection Failed
                                                        </span>
                                                      ) : item.isConnected ? (
                                                        <span
                                                          className='badge badge-light-success px-3 py-2 fs-9 d-flex justify-content-center'
                                                          style={{width: '120px'}}
                                                        >
                                                          Connected
                                                        </span>
                                                      ) : (
                                                        <span
                                                          className='badge badge-light-danger px-3 py-2 fs-9 d-flex justify-content-center'
                                                          style={{width: '120px'}}
                                                        >
                                                          Not Connected
                                                        </span>
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              )
                                            )
                                          ) : (
                                            <tr>
                                              <td colSpan={6}>
                                                <div className='py-2 d-flex flex-column align-content-center justify-content-center'>
                                                  <div className='text-center'>
                                                    <div className='symbol symbol-150px'>
                                                      <img src='/media/other/nodata.png' alt='' />
                                                    </div>
                                                  </div>
                                                  <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                                                    No matching records found
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>

                                        {/* end::Table body */}
                                      </table>
                                      {/* end::Table */}
                                    </div>
                                  </>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* begin::Body */}
      {!hideTable && (
      <div className='py-0'>
        {/* begin::Table container */}
        <div className='table-responsive'>
          {/* begin::Table */}
          <table
            className='table table-row-dashed table-row-gray-300 align-middle gs-3 gy-3'
            ref={tableRef}
          >
            {/* begin::Table head */}
            <thead>
              <tr className='fw-semibold text-muted text-uppercase'>
                <th className='min-w-200px '>Call ID</th>
                {showAiQcColumn && <th className='min-w-120px'>AI QC Status</th>}
                {title === 'OPI Logs' && <th className='min-w-150px '>Call Type</th>}
                {(title === 'Scheduled Log' ||
                  title === 'In Person Log' ||
                  title === 'Customer Un-Invoiced Calls' ||
                  title === 'Interpreter Un-Invoiced Calls') && (
                  <th className='min-w-200px '>APPOINTMENT ID</th>
                )}
                {title !== 'Operator Log' && title !== 'Backstop Log' && (
                  <th className='min-w-150px'>
                    <span className='d-flex align-items-center'>
                      Call Status{' '}
                      <a
                        type='button'
                        className=''
                        data-toggle='tooltip'
                        data-placement='top'
                        title='Connected - call includes an interpretation duration. Abandoned - call does not include an operator or interpretation duration.'
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen045.svg'
                          className='svg-icon-muted text-primary'
                        />
                      </a>
                    </span>
                  </th>
                )}
                {isSystemUser && <th className='min-w-150px '>Call Ended By</th>}
                {title !== 'Operator Log' && title !== 'Backstop Log' && (
                  <th className='min-w-150px '>{isConsumer ? 'Interpreter ID' : 'Interpreter'}</th>
                )}
                {!isConsumer && title !== 'Operator Log' && title !== 'Backstop Log' && (
                  <th className='min-w-100px '>Interpreter ID</th>
                )}
                <th className='min-w-200px '>Date Time</th>
                <th className='min-w-200px '>company Name</th>
                <th className='min-w-150px '>Requester</th>
                <th className='min-w-150px '>Requester Number</th>
                <th className='min-w-150px '>Available Agents</th>
                {title !== 'Operator Log' && title !== 'Backstop Log' && (
                  <th className='min-w-200px'>
                    <span className='d-flex align-items-center'>
                      Interpreter Rating{' '}
                      <a
                        type='button'
                        className=''
                        data-toggle='tooltip'
                        data-placement='top'
                        title='Interpreter rating / call quality rating submitted by requestor'
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen045.svg'
                          className='svg-icon-muted text-primary'
                        />
                      </a>
                    </span>
                  </th>
                )}
                {title !== 'Backstop Log' && (
                  <th className='min-w-200px '>
                    <span className='d-flex align-items-center'>
                      Requester Rating
                      <a
                        type='button'
                        className=''
                        data-toggle='tooltip'
                        data-placement='top'
                        title='Requestor rating / call quality rating submitted by interpreter'
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen045.svg'
                          className='svg-icon-muted text-primary'
                        />
                      </a>
                    </span>
                  </th>
                )}

                <th className='min-w-150px '>Language</th>
                <th className='min-w-150px '>Type</th>
                {!isConsumer && <th className='min-w-150px '>Backstop Partner</th>}
                {title !== 'In Person Log' && (
                  <th className='min-w-200px '>
                    <span className='d-flex align-items-center'>
                      Queue / Hold Time
                      <a
                        type='button'
                        className=''
                        data-toggle='tooltip'
                        data-placement='top'
                        title='Call Center Queue/Hold Time is added in parenthesis next and represents the time a requestor waiting on hold with a BPIN Call Center. This column is listed separately in CSV exports as Call Center Queue/Hold Time'
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen045.svg'
                          className='svg-icon-muted text-primary'
                        />
                      </a>
                    </span>
                  </th>
                )}
                {(title === 'On Demand Log' || title === 'Scheduled Log') && (
                  <th className='min-w-150px '>Connecting Time</th>
                )}
                <th className='min-w-150px '>
                  {title === 'OPI Logs' ? 'Total Duration (m)' : 'Total Duration'}
                </th>
                {title !== 'Backstop Log' && (
                  <th className='min-w-150px '>
                    {title === 'OPI Logs' ? 'Agent Duration (m)' : 'Agent Duration'}
                  </th>
                )}
                {title !== 'Operator Log' && title !== 'Backstop Log' && (
                  <th className='min-w-200px '>Other Participants</th>
                )}
                {title !== 'Backstop Log' && (
                  <th className='min-w-200px '>
                    {title === 'OPI Logs'
                      ? 'Total Conference Duration (m)'
                      : 'Total Conference Duration'}
                  </th>
                )}
                <th colSpan={2} className='min-w-500px text-start'>
                  <table className='w-100 mb-0'>
                    <tbody>
                      <tr>
                        <td
                          className='border-0 text-start align-middle p-0 pe-3'
                          style={{width: '50%'}}
                        >
                          <span>Questions</span>
                        </td>
                        <td className='border-0 text-start align-middle p-0' style={{width: '50%'}}>
                          <span>Answers</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </th>

                {/* <th className='min-w-150px text-end'>Action</th> */}
              </tr>
            </thead>
            {/* end::Table head */}
            {/* begin::Table body */}
            <tbody>
              {!isLoading && call_log?.length > 0 ? (
                call_log?.map((item: any, index: number) => (
                  <tr key={item?.code ?? item?.callId ?? index}>
                    <td>
                      <a
                        href='#'
                        className='text-gray-800 text-hover-primary d-block fs-6'
                        data-bs-toggle='modal'
                        data-bs-target='#kt_call_in_details'
                        onClick={() => fetchCallInDetails(item.code, item.initialContactId, item)}
                      >
                        {item.code ?? '-'}
                      </a>
                      {getFkCallHeaderReference(item) && (
                        <span className='text-muted fs-8 d-block'>
                          Ref:{' '}
                          <a
                            href='#'
                            className='text-primary'
                            data-bs-toggle='modal'
                            data-bs-target='#kt_call_in_details'
                            onClick={(e) => {
                              e.preventDefault()
                              openReferenceCall(getFkCallHeaderReference(item)!, item)
                            }}
                          >
                            {formatReferenceCallId(getFkCallHeaderReference(item)!, item)}
                          </a>
                        </span>
                      )}
                    </td>
                    {showAiQcColumn && (
                      <td>
                        <AiQcStatusBadge
                          status={item?.aiQcStatus ?? item?.AiQcStatus ?? null}
                          nameMentioned={
                            item?.aiQcNameMentioned ?? item?.AiQcNameMentioned ?? null
                          }
                          languageMentioned={
                            item?.aiQcLanguageMentioned ??
                            item?.AiQcLanguageMentioned ??
                            null
                          }
                          idMentioned={
                            item?.aiQcIdMentioned ?? item?.AiQcIdMentioned ?? null
                          }
                        />
                      </td>
                    )}
                    {title === 'OPI Logs' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {item.frontendCallType || '-'}
                        </a>
                      </td>
                    )}
                    {(title === 'Scheduled Log' ||
                      title === 'In Person Log' ||
                      title === 'Customer Un-Invoiced Calls' ||
                      title === 'Interpreter Un-Invoiced Calls') && (
                      <td>
                        {item.appointmentId === 0 ? (
                          <a className='text-gray-800 text-hover-primary d-block fs-6'>-</a>
                        ) : (
                          <a
                            className='text-gray-800 text-hover-primary d-block fs-6 cursor-pointer'
                            onClick={() => navigate(`/appointmentViewer/${item.appointmentId}`)}
                          >
                            {item.appointmentId ?? '-'}
                          </a>
                        )}
                      </td>
                    )}
                    {title !== 'Operator Log' && title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {item.callStatus ?? '-'}
                        </a>
                      </td>
                    )}
                    {isSystemUser && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {item.callEndedBy ?? item.CallEndedBy ?? '-'}
                        </a>
                      </td>
                    )}
                    {title !== 'Operator Log' && title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {getInterpreterDisplay(item)}
                        </a>
                      </td>
                    )}
                    {!isConsumer && title !== 'Operator Log' && title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {getInterpreterIdDisplay(item)}
                        </a>
                      </td>
                    )}
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {item.initiationTimestamp ?? '-'}
                      </a>
                    </td>
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {item.companyName ?? '-'}
                      </a>
                    </td>
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {item.customer ?? '-'}
                      </a>
                    </td>
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {item.requesterPhoneNumber ?? '-'}
                      </a>
                    </td>
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {item.availableAgentCount ?? item?.AvailableAgentCount ?? '-'}
                      </a>
                    </td>
                    {title !== 'Operator Log' && title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>-</a>
                      </td>
                    )}
                    {title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>-</a>
                      </td>
                    )}

                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {formatLanguageDisplay(item.languages ?? item.toLanguage)}
                      </a>
                    </td>
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {formatChannelDisplay(item.channel ?? item.communicationType)}
                      </a>
                    </td>
                    {!isConsumer && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {getBackstopPartnerDisplay(item)}
                        </a>
                      </td>
                    )}
                    {title !== 'In Person Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {item.queueHoldTime
                            ? typeof item.queueHoldTime === 'string'
                              ? item.queueHoldTime
                              : formatSecondsToHHMMSS(item.queueHoldTime)
                            : '-'}
                        </a>
                      </td>
                    )}
                    {(title === 'On Demand Log' || title === 'Scheduled Log') && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {item.connectingTimeSeconds != null && item.connectingTimeSeconds >= 0
                            ? formatSecondsToHHMMSS(item.connectingTimeSeconds)
                            : '-'}
                        </a>
                      </td>
                    )}
                    <td>
                      <a className='text-gray-800 text-hover-primary d-block fs-6'>
                        {title === 'OPI Logs'
                          ? formatToBillingMinutes(item.totalCallDuration)
                          : item.totalCallDuration
                          ? typeof item.totalCallDuration === 'string'
                            ? item.totalCallDuration
                            : formatSecondsToHHMMSS(item.totalCallDuration)
                          : '-'}
                      </a>
                    </td>
                    {title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {title === 'OPI Logs'
                            ? formatToBillingMinutes(item.agentCallDuration)
                            : item.agentCallDuration
                            ? typeof item.agentCallDuration === 'string'
                              ? item.agentCallDuration
                              : formatSecondsToHHMMSS(item.agentCallDuration)
                            : '-'}
                        </a>
                      </td>
                    )}
                    {title !== 'Operator Log' && title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {item.otherParticipants ?? '-'}
                        </a>
                      </td>
                    )}
                    {title !== 'Backstop Log' && (
                      <td>
                        <a className='text-gray-800 text-hover-primary d-block fs-6'>
                          {title === 'OPI Logs'
                            ? formatToBillingMinutes(item.conferenceDuration)
                            : item.conferenceDuration
                            ? typeof item.conferenceDuration === 'string'
                              ? item.conferenceDuration
                              : formatSecondsToHHMMSS(item.conferenceDuration)
                            : '-'}
                        </a>
                      </td>
                    )}

                    {/* Questions and Answers — paired rows so wrapping stays aligned */}
                    <td colSpan={2} className='text-start text-gray-800 fs-6'>
                      {renderQuestionAnswerPairs(item.questions, item.answers)}
                    </td>
                    {/* <td>
                      <div className='d-flex flex-end'>
                        <a
                          className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                          data-bs-toggle='modal'
                          data-bs-target='#kt_call_in_details'
                        >
                          <KTSVG
                            path='/media/icons/duotune/art/art005.svg'
                            className='svg-icon-3'
                          />
                        </a>
                      </div>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={14}>
                    <div className='py-5 d-flex flex-column align-content-center justify-content-center'>
                      <div className='text-center'>
                        <div className='symbol symbol-200px '>
                          <img src='/media/other/nodata.png' alt='' />
                        </div>
                      </div>
                      <div className='d-flex text-center w-100 align-content-center justify-content-center fw-semibold fs-3 text-gray-400'>
                        No call logs available
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {/* end::Table body */}
          </table>
          {/* end::Table */}
        </div>
        {/* end::Table container */}
      </div>
      )}
      {/* begin::Body */}
      {(isLoading || isCallDetailsLoading) && <CommonLoading />}
    </div>
  )
}

export {LogsViewTable}
